import {
  BackEndValueType,
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IDatabaseTableModel,
  IDisplayItem,
  IGenericSelectTablesModel,
  IImageLibraryDto,
  IProperty,
  IScreenDesign,
  ISymbol
} from "@kuika/kuika-cl-model";
import classNames from "classnames";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import {
  formatQualifiedName,
  getActionDatasourceQualifier
} from "../../../../shared/utility/datasource-name-qualifier";
import { PropertyHelper, UnitInputPropertyName } from "../../../../shared/utility/property-helper";
import { getResultActionTag } from "../../../../shared/utility/result-action-tag";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import { KuikaTag } from "../../../ui-kit/kuika-tag/kuika-tag";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { ParameterConditionValueEditor } from "./parameter-condition-value-editor";
import { PropertyFormColorPickerProperty } from "./property-form-color-picker-property";
import { PropertyFormDateTimeProperty } from "./property-form-datetime-property";
import { PropertyFormImage } from "./property-form-image";
import { PropertyFormInputNumberProperty } from "./property-form-input-number-property";
import { PropertyFormInputProperty } from "./property-form-input-property";
import { PropertyFormInputWithSelectProperty } from "./property-form-input-with-select-property";
import { IPropertyFormSelectPropertyOption, PropertyFormSelectProperty } from "./property-form-select-property";
import { PropertyFormSwitchProperty } from "./property-form-switch-property";
import { SymbolPickerCascader } from "./symbol-picker-cascader";

export interface ISymbolPickerState {}

export interface ISymbolPickerProps {
  symbol?: ISymbol;
  valueType?: BackEndValueType;
  onSelectedSymbol: (symbol: ISymbol, changeReason?: ESdChangeReason) => void;
  targetCardinality?: ECardinality;
  targetDI?: IDisplayItem;
  isValueProp?: boolean;
  changeType?: "onBlur" | "onKeyPress";
  isWidthOrHeightProp?: UnitInputPropertyName;
  hideFixedValue?: boolean;
  hideWFParams?: boolean;
  hideCategoryChange?: boolean;
  /** KOA-17739: offer the "Condition" category (conditional parameter value). Action-parameter hosts only. */
  allowConditionValue?: boolean;
  action?: IAction;
  parameter?: IActionParameter;
  /**
   * Preview-only fallback for the "Binding to" header when a host edits a named action parameter's
   * symbol but does not pass `parameter` (a hand-built symbols group such as Notify or Send Mail).
   * Never read for option generation, test ids, or the Condition-value editor's input choice — those
   * all key off `parameter`. Passing the real `IActionParameter` here would change behaviour (test id
   * precedence, the engine's `isEnum`/`isObject`/`cardinality`/`ownerPropName` reads), so this is a
   * separate, inert prop.
   */
  bindingParameter?: IActionParameter;
  /**
   * Overrides the preview header's name when it must match a title the host already renders (e.g.
   * Notify's "Message" for a parameter literally named `message`). Omit when the parameter's
   * `displayName ?? name` already reads correctly.
   */
  bindingLabel?: string;
  inputRef?: React.RefObject<any>;
  hideActionResults?: boolean;
  showInSingleLine?: boolean;
  /**
   * Keep the popup's box fixed and scroll its columns instead of letting it grow and be repositioned.
   *
   * For callers with room to their right — a canvas rather than a side panel. Without it, antd re-measures the popup
   * as each column opens and shifts it sideways, moving the row out from under the cursor mid-selection. Panel
   * pickers are pinned by `.sticky-symbol-picker` instead and must keep opening leftwards.
   *
   * The columns popup (`symbolPickerColumnsV2`) reads it as "this caller sits mid-screen" and opens compact — no
   * preview pane, narrower columns — as it does on its own inside a modal or a popover.
   */
  stablePopup?: boolean;
  isGenericSelect?: boolean;
  isObject?: boolean;
  minValue?: number;
  pickerIcon?: React.ReactNode;
  popupPlacement?: "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
  disabled?: boolean;
  parameterOptions?: IPropertyFormSelectPropertyOption[];
  parameterSelectboxDropdownHeader?: React.ReactNode;
  parameterSelectboxDropdownHeaderClick?: () => void;
  parameterSelectboxOptionRenderer?: (option: IPropertyFormSelectPropertyOption) => React.ReactNode;
  parameterSelectboxIsLoading?: boolean;
  isMultiSelect?: boolean;
  isColorPicker?: boolean;
  property?: IProperty;
  trimValue?: boolean;
  /** The condition editor's Field picker — the left-hand side of a comparison. */
  isConditionPicker?: boolean;
  /**
   * The condition editor's Value picker — the right-hand side of a comparison. Its fixed value is
   * written straight into the saved condition and read back by the generated code, so a numeric one
   * is edited as typed (see `numberInput`) rather than through the property panel's rounding input.
   */
  isConditionValuePicker?: boolean;
  parameterSelectClassName?: string;
  key?: string;
  data_testid?: string;
  hideMiInstanceCountParam?: boolean;
  // For generic select calculated fields - table fields support
  genericSelectTables?: IGenericSelectTablesModel[];
  genericSelectTableModels?: IDatabaseTableModel[];
}

class SymbolPicker extends PureSdComponent<ISymbolPickerProps & IWithSDProps & IWithContextProp, ISymbolPickerState> {
  propertyHelper: PropertyHelper;

  constructor(props: ISymbolPickerProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.propertyHelper = new PropertyHelper();
  }

  public static defaultProps = {
    changeType: "onKeyPress"
  };

  changeActionParameterBySymbol = (symbol: ISymbol) => {
    if (symbol.category === ESymbolCategory.fixed) {
      symbol.valuePlaceholderText = "";
    }
    this.props.onSelectedSymbol(symbol);
  };

  changeActionParameterByFixedValue = (value: any, symbol: ISymbol) => {
    if (symbol.fixedValue !== value || symbol.isFixed !== true) {
      if (this.props.trimValue) {
        value = value?.replace(/(?:^(?:&nbsp;)+)|(?:(?:&nbsp;)+$)/g, "");
        value = value?.trim();
      }
      symbol.fixedValue = value;
      symbol.isFixed = true;
      this.props.onSelectedSymbol(symbol, ESdChangeReason.ActionParameterFixedValueChanged);
    }
  };

  changeImageParameter = (value: any, symbol: ISymbol, image: IImageLibraryDto) => {
    symbol.fixedValue = image.id;
    // symbol.fixedValue = value;

    symbol.isFixed = true;
    this.props.onSelectedSymbol(symbol);
  };

  changeActionFieldToDisplay = (fieldToDisplay: string, symbol: ISymbol) => {
    symbol.fieldToDisplay = fieldToDisplay;
    this.props.onSelectedSymbol(symbol);
  };

  changeActionFieldToUseAsKey = (fieldToUseAsKey: string, symbol: ISymbol) => {
    symbol.fieldToUseAsKey = fieldToUseAsKey;
    this.props.onSelectedSymbol(symbol);
  };

  changeActionFieldToUseAsParent = (fieldToUseAsParent: string, symbol: ISymbol) => {
    symbol.fieldToUseAsParent = fieldToUseAsParent;
    this.props.onSelectedSymbol(symbol);
  };

  selectboxInput = (
    symbol: ISymbol,
    options: { label: string; value: string | number; isDefault?: boolean }[],
    isMultiSelect = false
  ) => {
    return (
      <PropertyFormSelectProperty
        mode={isMultiSelect ? "multiple" : undefined}
        value={symbol.fixedValue?.toString()}
        options={options}
        onChange={(value) => this.changeActionParameterByFixedValue(value, symbol)}
        dropdownHeader={this.props.parameterSelectboxDropdownHeader}
        onDropdownHeaderClick={this.props.parameterSelectboxDropdownHeaderClick}
        optionRenderer={this.props.parameterSelectboxOptionRenderer}
        isLoading={this.props.parameterSelectboxIsLoading}
        sd={this.props.sd}
        selectClassName={this.props.parameterSelectClassName}
      />
    );
  };

  // Name of the fixed-value box, mirroring SymbolPickerCascader.getTestIdBase: a component
  // property is named by the property, and an action parameter — which has no property to be
  // named by — falls back to the picker's explicit `data_testid` (Set Value Of's value box is
  // the first such caller). Unnamed pickers keep emitting no attribute at all.
  fixedValueInputTestId = (): string | undefined => {
    const base: string | undefined = this.props.property?.name ?? this.props.data_testid;
    return base ? `property-input-${base}` : undefined;
  };

  stringInput = (symbol: ISymbol) => {
    return (
      <>
        <PropertyFormInputProperty
          value={symbol.fixedValue}
          changeType={this.props.changeType}
          onChange={(value: string) => {
            this.changeActionParameterByFixedValue(value, symbol);
          }}
          updateDebounce={250}
          shouldFocus={this.props.isValueProp === true}
          disabled={this.props.disabled}
          testId={this.fixedValueInputTestId()}
        />
        {this.props.isValueProp === true && symbol.category !== ESymbolCategory.fixed && (
          <KuikaInput
            defaultValue={symbol.valuePlaceholderText}
            placeholder="Design Placeholder"
            className="kuika-symbol-picker__design-placeholder"
            onBlur={(e) => this.handleSymbolPlaceholderChange(symbol, e.target.value)}
            disabled={this.props.disabled}
          />
        )}
      </>
    );
  };

  colorPickerInput = (symbol: ISymbol) => {
    return (
      <>
        <PropertyFormColorPickerProperty
          symbol={symbol}
          applyPropertyValueChanged={(value: any) => {
            this.changeActionParameterByFixedValue(value, symbol);
          }}
          dontUseFixedPosition
          clearsToEmpty={this.props.property?.name === "navigationButtonColor"}
        />
      </>
    );
  };

  changeWidthandHeightActionParameter = (value: any, symbol: ISymbol, type: string) => {
    symbol.fixedValue = PropertyHelper.composeUnitValue(value, type);
    symbol.isFixed = true;
    this.props.onSelectedSymbol(symbol);
  };

  stringInputWithSelect = (symbol: ISymbol) => {
    return (
      <>
        <PropertyFormInputWithSelectProperty
          value={
            this.props.isWidthOrHeightProp
              ? PropertyHelper.getTextValueForDisplayWidthorHeight(symbol.fixedValue, this.props.isWidthOrHeightProp)
              : symbol.fixedValue
          }
          widthOrHeightValue={
            this.props.isWidthOrHeightProp ? PropertyHelper.getWidthOrHeightValueForDisplay(symbol.fixedValue) : ""
          }
          changeType={this.props.changeType}
          onChange={(value: string) => {
            this.changeActionParameterByFixedValue(value, symbol);
          }}
          onWidthOrHeightChange={(value: string, type: string) => {
            this.changeWidthandHeightActionParameter(value, symbol, type);
          }}
          isWidthOrHeightProp={this.props.isWidthOrHeightProp}
          updateDebounce={250}
          disabled={this.props.disabled}
        />
      </>
    );
  };

  numberInput = (symbol: ISymbol) => {
    const isConditionValueContext: boolean = this.props.isConditionValuePicker === true;

    if (isConditionValueContext) {
      return (
        <KuikaInput
          type="number"
          // A condition's value is stored as typed and read again by generated code, so the field
          // must not round it: 9.99 has to stay 9.99, and stay the string it always was.
          keepTypedValue
          value={symbol.fixedValue}
          onChange={(e) => {
            let value: any = e.target.value;
            if (value === "NaN") {
              value = "";
            }
            this.changeActionParameterByFixedValue(value, symbol);
          }}
          style={{
            width: "100%",
            backgroundColor: "var(--dark-0)",
            border: "none",
            boxShadow: "none",
            outline: "none"
          }}
          disabled={this.props.disabled}
        />
      );
    }

    return (
      <>
        <PropertyFormInputNumberProperty
          updateDebounce={250}
          // The same `property-input-<name>` the text box carries, so a number property is
          // addressed like any other fixed value.
          testId={this.fixedValueInputTestId()}
          value={symbol.fixedValue}
          changeType={this.props.changeType}
          onChange={(value: string) => {
            if (value === "NaN") {
              value = "";
            }
            this.changeActionParameterByFixedValue(value, symbol);
          }}
          minValue={this.props.minValue}
          disabled={this.props.disabled}
        />
      </>
    );
  };

  dateTimeInput = (symbol: ISymbol, valueType: string) => {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    const selectedDI: IDisplayItem | undefined = this.props.sd.getSelectedDisplayItem(sd);
    return (
      <>
        <PropertyFormDateTimeProperty
          value={symbol.fixedValue}
          onChange={(value: string | undefined) => {
            this.changeActionParameterByFixedValue(value, symbol);
          }}
          disabled={this.props.disabled}
          // antd's "Select date" needs 74px; these rows give the field ~46px.
          placeholder={this.props.isGenericSelect ? "Select" : undefined}
          isDateTime={
            !(
              (selectedDI?.item.name === "Date" || selectedDI?.item.name === "DateRange") &&
              (this.props.isValueProp ||
                this.props.property?.name?.toLowerCase() === "value" ||
                this.props.property?.name.toLowerCase() === "disablebeforedate" ||
                this.props.property?.name.toLowerCase() === "disableafterdate")
            ) &&
            (valueType === "datetime" || valueType === "datetimeoffset")
          }
          isTime={
            this.props.property?.name.toLowerCase() === "disablebeforetime" ||
            this.props.property?.name.toLowerCase() === "disableaftertime" ||
            this.props.property?.name.toLowerCase() === "mintime" ||
            this.props.property?.name.toLowerCase() === "maxtime"
          }
        />
      </>
    );
  };

  booleanInput = (symbol: ISymbol) => {
    const base: string | undefined = this.testIdBase();
    const switchTestId: string | undefined = base ? `${base}Switch` : undefined;
    return (
      <>
        <div
          className="kuika-right-panel__col"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingRight: "4px"
          }}
        >
          <PropertyFormSwitchProperty
            value={symbol.fixedValue?.toString()?.toLowerCase() == "true"}
            onChange={(value: boolean) => {
              this.changeActionParameterByFixedValue(value, symbol);
            }}
            disabled={this.props.disabled}
            dataTestId={switchTestId}
          />
        </div>
      </>
    );
  };

  imageInput = (symbol: ISymbol) => {
    return (
      <>
        {/* A plain full-width column: the picker is a block that fills the row, not a small control
            pinned to its right edge like the switch above. Right-aligning it and insetting it by 4px
            pushed everything in the card — search box included — off centre, 2px of margin on the
            left against 6px on the right. */}
        <div className="kuika-right-panel__col">
          <PropertyFormImage
            onChange={(value: string, image: IImageLibraryDto) => {
              this.changeImageParameter(value, symbol, image);
            }}
            selectedId={symbol.fixedValue}
            disabled={this.props.disabled}
          />
        </div>
      </>
    );
  };

  renderInputForFixedValueBySymbol = (symbol: ISymbol) => {
    let formInput = <></>;
    if (this.props.isWidthOrHeightProp && this.props.valueType?.toLowerCase() === "string") {
      formInput = this.stringInputWithSelect(symbol);
      return formInput;
    }
    if (this.props.hideFixedValue === true) {
      return;
    }
    // List properties (e.g., number[], string[]) have no scalar fixed value
    const isList = this.props.property?.isList ?? this.props.property?.dynamicPropertyDefinition?.isList;
    if (isList) {
      return;
    }

    if (this.props.parameterOptions) {
      return this.selectboxInput(symbol, this.props.parameterOptions, this.props.isMultiSelect);
    }

    if (this.props.isColorPicker) {
      return this.colorPickerInput(symbol);
    }

    switch (this.props.valueType?.toLowerCase()) {
      case "string":
      case "unbound":
      case "any":
        formInput = this.stringInput(symbol);
        break;
      case "guid":
        formInput = this.stringInput(symbol);
        break;
      case "int16":
        formInput = this.numberInput(symbol);
        break;
      case "int32":
        formInput = this.numberInput(symbol);
        break;
      case "number":
        formInput = this.numberInput(symbol);
        break;
      case "int64":
        formInput = this.numberInput(symbol);
        break;
      case "decimal":
        formInput = this.numberInput(symbol);
        break;
      case "double":
        formInput = this.numberInput(symbol);
        break;
      case "single":
        formInput = this.numberInput(symbol);
        break;
      case "boolean":
        formInput = this.booleanInput(symbol);
        break;
      case "image":
        formInput = this.stringInput(symbol);
        break;
      case "date":
      case "datetime":
      case "datetimeoffset":
        formInput = this.dateTimeInput(symbol, this.props.valueType?.toLowerCase() ?? "date");
        break;
      case "colorpicker":
        formInput = this.colorPickerInput(symbol);
        break;
    }
    return formInput;
  };

  renderInputForUploadedImagesBySymbol = (symbol: ISymbol) => {
    let formInput = <></>;
    formInput = this.imageInput(symbol);
    return formInput;
  };

  getCategoryName = (symbolPickerNodeCategory: ESymbolCategory): string => {
    switch (symbolPickerNodeCategory) {
      case ESymbolCategory.fixed:
        return "Fixed Value";
      case ESymbolCategory.condition:
        return "Condition";
      default:
        return ESymbolCategory[symbolPickerNodeCategory];
    }
  };

  getSymbolDescription = (symbol: ISymbol) => {
    let result = symbol.description;

    if (symbol?.actionDefinitionId) {
      const actionDefinition = this.props.sd.actionDefinitions?.find((ad) => ad.id === symbol?.actionDefinitionId);
      if (actionDefinition) {
        const firstIndex = result?.indexOf("[");
        const lastIndex = result?.lastIndexOf("]");
        if (firstIndex > -1 && lastIndex > -1) {
          result = `${result?.substring(0, firstIndex)}[${
            actionDefinition.displayName ?? actionDefinition.name
          }]${result?.substring(lastIndex + 1)}`;
          // "[CRM_DB] [GetUsers].Name" while another datasource holds the same action name
          // (KOA-18147). The label only: `symbol.description` itself stays as the generator reads it.
          result = formatQualifiedName(
            result,
            getActionDatasourceQualifier(actionDefinition, this.props.sd.actionDefinitions)
          );
        }
      }
    }

    const actionDefinitionRegex =
      /\[actionDefinitionId:([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[0-9a-f]{4}-[0-9a-f]{12})\]/i;
    const actionDefinitionRegexMatch = symbol.description?.match(actionDefinitionRegex);
    if (actionDefinitionRegexMatch) {
      const actDefId = actionDefinitionRegexMatch[1];
      const actionDefinition = this.props.sd.actionDefinitions.find((x) => x.id === actDefId);
      if (actionDefinition) {
        result = result.replace(actionDefinitionRegex, actionDefinition.displayName ?? actionDefinition.name);
        return result;
      }
    }

    const diIdRegex = /\[diId:([0-9]+)\]/;
    const diIdMatch = symbol.description?.match(diIdRegex);
    if (result && diIdMatch) {
      const matchedDiId = diIdMatch[1];
      const matchedDi = this.props.sd.screenDesignDictionary[parseInt(matchedDiId, 10)];
      if (matchedDi) {
        const matchedDiLabel = matchedDi.friendlyName
          ? matchedDi.friendlyName
          : matchedDi.item.name
            ? matchedDi.item.name
            : matchedDi.item.id
              ? matchedDi.item.id
              : "";
        result = result.replace(diIdRegex, matchedDiLabel);
        return result;
      }
    }

    return result;
  };

  getSymbol = (): ISymbol => {
    let symbol: ISymbol | undefined;
    let fixedValue = "" as any;
    if (this.props.valueType?.toLowerCase() === "boolean") {
      fixedValue = this.props.property?.defaultValue?.toString()?.toLowerCase() === "true";
    }
    if (this.props.symbol && !_.isEmpty(this.props.symbol)) {
      symbol = this.props.symbol;
      if (symbol.fixedValue === undefined) {
        symbol.fixedValue = fixedValue;
      }
    } else {
      symbol = {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue,
        description: ""
      } as ISymbol;
    }

    return symbol;
  };

  /**
   * Name every one of this picker's test-ids is built from.
   *
   * `parameter` / `property` cover the generic action-parameter and component-property paths and
   * keep precedence, so ids tests already rely on are unchanged. `data_testid` is the explicit
   * opt-in for a picker that has neither — the Navigate action's target-screen-input rows are that
   * shape, and without it every row on one action shared the anonymous id `boundValue`, so no test
   * could address one input among several (QA-423). The fixed-value box of those rows is named by
   * `fixedValueInputTestId` above, which resolves the same explicit id into `property-input-<base>`.
   *
   * Mirrors `SymbolPickerCascader.getTestIdBase`, which resolves the same three sources in the same
   * order for `<base>SymbolPicker` / `<base>SymbolPickerInput`.
   */
  testIdBase = (): string | undefined => {
    return this.props.parameter?.name ?? this.props.property?.name ?? this.props.data_testid;
  };

  renderSymbolDescription = (symbol: ISymbol) => {
    const description = this.getSymbolDescription(symbol);
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    const selectedDI: IDisplayItem | undefined = this.props.sd.getSelectedDisplayItem(sd);

    // Two results of the same action read alike ("[GetFromBag]"); only the action's tag tells them
    // apart, so it is drawn beside the value the way the picker's dropdown draws it (KOA-16209).
    const actionTag: string | undefined = getResultActionTag(this.props.sd, symbol.resultActionId);
    const tagSuffix: string = actionTag ? `#${actionTag}` : "";

    // Scope the resolved-binding label by the property/parameter/explicit name so a test can read a
    // specific picker's bound value (e.g. "valueBoundValue"); the ".kuika_rightPanelDIName" class
    // alone is shared by every picker. A picker with no name at all keeps the bare "boundValue" id.
    const base: string | undefined = this.testIdBase();
    const boundValueTestId: string = base ? `${base}BoundValue` : "boundValue";

    const boundValue: JSX.Element = (
      <KuikaTooltip content={`${description}${tagSuffix}`}>
        <label
          data-testid={boundValueTestId}
          className="k_Regular-G1-Left kuika_rightPanelDIName"
          style={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden"
          }}
        >
          {description}
        </label>
      </KuikaTooltip>
    );

    return (
      <>
        {actionTag ? (
          <div className="kuika-symbol-picker__bound-value">
            {boundValue}
            <KuikaTag type="action" label={actionTag} dataTestId={`${boundValueTestId}Tag`} truncate="middle" />
          </div>
        ) : (
          boundValue
        )}
        {this.props.isValueProp === true &&
          description?.length > 0 &&
          symbol.category !== ESymbolCategory.fixed &&
          selectedDI?.item.name !== "Image" &&
          selectedDI?.item.name !== "Thumbnail" && (
            <KuikaInput
              defaultValue={symbol.valuePlaceholderText}
              placeholder="Design Placeholder"
              className="kuika-symbol-picker__design-placeholder"
              onBlur={(e) => this.handleSymbolPlaceholderChange(symbol, e.target.value)}
            />
          )}
      </>
    );
  };

  renderInputForSqlParameter = (symbol) => {
    return (
      <KuikaInput
        defaultValue={symbol.sqlParamName}
        // "SQL Parameter" needs 99.8px against ~66px here; see generic-sql-modal.scss.
        placeholder={this.props.isGenericSelect ? "SQL Param" : "SQL Parameter"}
        style={{ marginTop: 12, width: 190 }}
        onBlur={(e) => this.handleSymbolSqlParameterChange(symbol, e.target.value)}
        allowClear
        disabled={this.props.disabled}
      />
    );
  };

  handleSymbolPlaceholderChange = (symbol: ISymbol, val: string) => {
    symbol.valuePlaceholderText = val?.trim();
    if (symbol.valuePlaceholderText == "") {
      delete symbol.valuePlaceholderText;
    }
    this.props.onSelectedSymbol(symbol);
  };

  handleSymbolSqlParameterChange = (symbol: ISymbol, val: string) => {
    symbol.sqlParamName = val?.trim();
    this.props.onSelectedSymbol(symbol);
  };

  // KOA-17739: guard + Then/Else pickers for a conditional parameter value.
  renderInputForConditionValue = (symbol: ISymbol) => {
    return (
      <ParameterConditionValueEditor
        symbol={symbol}
        valueType={this.props.valueType}
        targetCardinality={this.props.targetCardinality}
        action={this.props.action}
        parameter={this.props.parameter}
        disabled={this.props.disabled}
        onChange={(updatedSymbol: ISymbol, changeReason?: ESdChangeReason) =>
          this.props.onSelectedSymbol(updatedSymbol, changeReason)
        }
      />
    );
  };

  renderInputs = (symbol: ISymbol) => {
    return symbol!.category === ESymbolCategory.fixed ? (
      this.renderInputForFixedValueBySymbol(symbol!)
    ) : symbol!.category === ESymbolCategory.uploadedImages ? (
      this.renderInputForUploadedImagesBySymbol(symbol!)
    ) : symbol!.category === ESymbolCategory.sqlParameter ? (
      this.renderInputForSqlParameter(symbol!)
    ) : symbol!.category === ESymbolCategory.condition ? (
      this.renderInputForConditionValue(symbol!)
    ) : (
      <div style={{ width: "100%" }}>{this.renderSymbolDescription(symbol)}</div>
    );
  };

  render() {
    const { showInSingleLine } = this.props;
    const symbol = this.getSymbol();
    return (
      <div
        className={classNames("kuika_styling_typography_wrapper", {
          kuika_single_line_symbol_picker: showInSingleLine,
          "kuika_symbol-picker__disabled": this.props.disabled
        })}
        style={{ width: "100%" }}
        id={this.props.key}
      >
        {(this.props.hideCategoryChange == undefined || this.props.hideCategoryChange == false) && (
          <SymbolPickerCascader
            symbol={symbol}
            categoryName={this.getCategoryName(symbol!.category)}
            targetTypeName={this.props.valueType}
            targetCardinality={this.props.targetCardinality}
            onSymbolPicked={(symbol: ISymbol) => {
              return this.changeActionParameterBySymbol(symbol);
            }}
            targetDI={this.props.targetDI}
            hideFixedValue={this.props.hideFixedValue}
            allowConditionValue={this.props.allowConditionValue}
            hideWFParams={this.props.hideWFParams}
            action={this.props.action}
            parameter={this.props.parameter}
            bindingParameter={this.props.bindingParameter}
            bindingLabel={this.props.bindingLabel}
            hideActionResults={this.props.hideActionResults}
            showInSingleLine={showInSingleLine}
            stablePopup={this.props.stablePopup}
            isGenericSelect={this.props.isGenericSelect}
            isObject={this.props.isObject ?? false}
            pickerIcon={this.props.pickerIcon}
            popupPlacement={this.props.popupPlacement}
            disabled={this.props.disabled}
            isConditionPicker={this.props.isConditionPicker}
            data_testid={this.props.data_testid}
            hideMiInstanceCountParam={this.props.hideMiInstanceCountParam}
            genericSelectTables={this.props.genericSelectTables}
            genericSelectTableModels={this.props.genericSelectTableModels}
            property={this.props.property}
          >
            {this.renderInputs(symbol)}
          </SymbolPickerCascader>
        )}
        {this.props.hideCategoryChange && this.renderInputs(symbol)}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangePropertyType ||
      changeReason === ESdChangeReason.ChangedCustomFieldOnPropertyPanel ||
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangePropertyValue ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const symbolPicker = withSD<ISymbolPickerProps>(withContext<ISymbolPickerProps>(SymbolPicker));
export { symbolPicker as SymbolPicker };
